/**
 * ENG-8441: the `knownSkillHashes` contract for INTEGRATION skill bundles.
 *
 * The manager writes an integration's skill folder to disk only when the bundle
 * content changes, using `knownSkillHashes` as a "what I last wrote to disk"
 * record. That is two cooperating sites — the installer that consults and sets
 * the entry, and the orphan sweep that deletes the folder — bound by an implicit
 * contract with nothing holding them together.
 *
 * They drifted. The sweep deleted folders and never cleared the cache, so the
 * cache went on asserting a folder that was no longer there. When the
 * integration came back, the installer recomputed the SAME bundle hash, matched
 * the stale entry, skipped the write, and the skill never returned.
 *
 * The resulting state is STABLE, not transient, which is why it does not
 * self-heal: once the integration is back in `currentIntegrationSkillIds` the
 * sweep stops deleting it, and the hash guard stops it being re-created. The two
 * mechanisms deadlock, and (the cache being process-local) only a manager
 * restart breaks the tie.
 *
 * Observed in prod on scout / agt-aws-1, 2026-08-04: `integration-vercel` was
 * swept while its integration row was mid-transition between quarantined and
 * re-enabled, returned to the refresh payload one poll later, and never came
 * back to disk. It was also invisible — the console renders the control plane's
 * view, so the agent's card kept listing a skill the host no longer had.
 *
 * These helpers exist so the key shape lives in ONE place and "forget on
 * delete" is the only way to remove a folder's record. A future sweep site
 * cannot silently reintroduce the drift by hand-rolling the key.
 *
 * The equivalent invariant for GLOBAL skills is already maintained inline
 * (`knownSkillHashes.delete('global-skill:…')` per `plan.removes`); this is the
 * integration-path counterpart.
 */

import { join } from 'node:path';

/** The mutable cache the manager threads through as `agentState.knownSkillHashes`. */
export type SkillHashCache = Map<string, string>;

/**
 * Cache key for one agent's integration skill bundle.
 *
 * The `plugin-skill:` prefix predates the ENG-4554 plugin→integration rename and
 * is a committed on-the-wire-ish contract only in the sense that install and
 * sweep must agree; it is process-local state, never persisted (confirmed absent
 * from `manager-state.json`), so the literal is free to change as long as it
 * changes HERE, once.
 */
export function integrationSkillHashKey(agentId: string, skillId: string): string {
  return `plugin-skill:${agentId}:${skillId}`;
}

/**
 * Should the installer write this bundle to disk? True when we have no record of
 * the folder, or the record disagrees with the bundle we just built.
 */
export function shouldWriteIntegrationSkill(
  cache: SkillHashCache,
  agentId: string,
  skillId: string,
  contentHash: string,
): boolean {
  return cache.get(integrationSkillHashKey(agentId, skillId)) !== contentHash;
}

/** Record that this bundle is now on disk. Call ONLY after a successful write. */
export function rememberIntegrationSkill(
  cache: SkillHashCache,
  agentId: string,
  skillId: string,
  contentHash: string,
): void {
  cache.set(integrationSkillHashKey(agentId, skillId), contentHash);
}

/**
 * Drop the record for a folder that is no longer on disk, so the next poll that
 * still wants it writes it again. Call from EVERY path that deletes a skill
 * folder — that is the whole point of this module.
 *
 * Safe for the legacy `plugin-<slug>/` folders the sweep also migrates away:
 * nothing ever writes a `plugin-skill:…:plugin-<slug>` key, so this is a no-op
 * for them and the ENG-4554 migration sweep still removes them for good.
 */
export function forgetIntegrationSkill(
  cache: SkillHashCache,
  agentId: string,
  skillId: string,
): void {
  cache.delete(integrationSkillHashKey(agentId, skillId));
}

/**
 * Delete one skill folder from every directory `installSkillFiles` may have
 * written it into, and drop its cache record.
 *
 * **The forget happens BEFORE the first delete, deliberately.** A Claude Code
 * install writes the same bundle to two trees (the agent's `skills/` and the
 * project's `.claude/skills/`), and the manager's whole cleanup block runs
 * inside one `try/catch`. Forgetting last means that if the first `rmSync`
 * succeeds and a later one throws, the exception unwinds past the forget: one
 * install target is now missing while the cache still claims the bundle is on
 * disk — which is exactly the ENG-8441 deadlock, reached by a different route.
 *
 * Ordering it first makes invalidation unconditional, and the two failure modes
 * are not symmetric:
 *
 * - forget-first, delete fails → we hold no record for a folder that IS still
 *   there. Next poll rewrites the same bytes over it. Idempotent, harmless.
 * - delete-first, forget skipped → folder gone, record says present, and it is
 *   never written again. Silent, and only a manager restart recovers it.
 *
 * `removeDir` is injected so the partial-failure path is testable without
 * contriving a read-only directory; the manager passes the real
 * existsSync/rmSync pair.
 */
export function removeIntegrationSkillFolder(opts: {
  cache: SkillHashCache;
  agentId: string;
  /** Folder name, e.g. `integration-vercel` (or a legacy `plugin-<slug>`). */
  entry: string;
  /** Every directory the folder may live in. */
  dirs: readonly string[];
  /** Remove one absolute path. May throw; the cache is already cleared by then. */
  removeDir: (path: string) => void;
}): void {
  forgetIntegrationSkill(opts.cache, opts.agentId, opts.entry);
  for (const dir of opts.dirs) {
    opts.removeDir(join(dir, opts.entry));
  }
}
